// Gradient Builder - Preset Gradients
// 27 curated gradients: 18 linear, 6 radial, 3 sweep

import type { GradientPreset } from "../model/gradient";

export const gradientPresets: GradientPreset[] = [
  // ============ LINEAR GRADIENTS (13) ============
  {
    id: "sunset-glow",
    name: "Sunset Glow",
    config: {
      type: "linear",
      angle: 135, // Top-left to bottom-right
      colors: [
        { color: "#FF6B35", offset: 0 },
        { color: "#E91E63", offset: 1 },
      ],
    },
    cssPreview: "linear-gradient(135deg, #FF6B35 0%, #E91E63 100%)",
  },
  {
    id: "ocean-blue",
    name: "Ocean Blue",
    config: {
      type: "linear",
      angle: 180, // Top to bottom
      colors: [
        { color: "#003F5B", offset: 0 },
        { color: "#00D4FF", offset: 1 },
      ],
    },
    cssPreview: "linear-gradient(180deg, #003F5B 0%, #00D4FF 100%)",
  },
  {
    id: "purple-midnight",
    name: "Purple Midnight",
    config: {
      type: "linear",
      angle: 90, // Left to right
      colors: [
        { color: "#2E1A47", offset: 0 },
        { color: "#FF006E", offset: 1 },
      ],
    },
    cssPreview: "linear-gradient(90deg, #2E1A47 0%, #FF006E 100%)",
  },
  {
    id: "tropical-horizon",
    name: "Tropical Horizon",
    config: {
      type: "linear",
      angle: 45, // Bottom-left to top-right
      colors: [
        { color: "#06D6A0", offset: 0 },
        { color: "#FF6B6B", offset: 1 },
      ],
    },
    cssPreview: "linear-gradient(45deg, #06D6A0 0%, #FF6B6B 100%)",
  },
  {
    id: "electric-blue",
    name: "Electric Blue",
    config: {
      type: "linear",
      angle: 135,
      colors: [
        { color: "#00D4FF", offset: 0 },     // Electric cyan
        { color: "#0066FF", offset: 0.5 },   // Bright blue
        { color: "#6B00FF", offset: 1 },     // Electric violet
      ],
    },
    cssPreview: "linear-gradient(135deg, #00D4FF 0%, #0066FF 50%, #6B00FF 100%)",
  },
  {
    id: "forest-dawn",
    name: "Forest Dawn",
    config: {
      type: "linear",
      angle: 225, // Top-right to bottom-left
      colors: [
        { color: "#0B3D2C", offset: 0 },
        { color: "#7FD99A", offset: 1 },
      ],
    },
    cssPreview: "linear-gradient(225deg, #0B3D2C 0%, #7FD99A 100%)",
  },
  {
    id: "cosmic-dust",
    name: "Cosmic Dust",
    config: {
      type: "linear",
      angle: 135, // Top-left to bottom-right
      colors: [
        { color: "#1A0533", offset: 0 },    // Deep purple
        { color: "#4A90D9", offset: 0.5 },  // Electric blue
        { color: "#E8B4D8", offset: 1 },    // Soft pink
      ],
    },
    cssPreview: "linear-gradient(135deg, #1A0533 0%, #4A90D9 50%, #E8B4D8 100%)",
  },
  {
    id: "autumn-fire",
    name: "Autumn Fire",
    config: {
      type: "linear",
      angle: 90, // Left to right
      colors: [
        { color: "#8B1E3F", offset: 0 },    // Deep red
        { color: "#E85D04", offset: 0.5 },  // Bright orange
        { color: "#FFBA08", offset: 1 },    // Golden yellow
      ],
    },
    cssPreview: "linear-gradient(90deg, #8B1E3F 0%, #E85D04 50%, #FFBA08 100%)",
  },
  {
    id: "northern-lights",
    name: "Northern Lights",
    config: {
      type: "linear",
      angle: 180, // Top to bottom
      colors: [
        { color: "#0D1B2A", offset: 0 },    // Deep navy
        { color: "#2EC4B6", offset: 0.5 },  // Emerald green
        { color: "#CBF3F0", offset: 1 },    // Soft cyan
      ],
    },
    cssPreview: "linear-gradient(180deg, #0D1B2A 0%, #2EC4B6 50%, #CBF3F0 100%)",
  },
  {
    id: "black-diamond",
    name: "Black Diamond",
    config: {
      type: "linear",
      angle: 135,
      colors: [
        { color: "#0A0A0A", offset: 0 },     // Deep black
        { color: "#2D2D2D", offset: 0.5 },   // Charcoal
        { color: "#4A4A4A", offset: 1 },     // Dark silver
      ],
    },
    cssPreview: "linear-gradient(135deg, #0A0A0A 0%, #2D2D2D 50%, #4A4A4A 100%)",
  },
  {
    id: "rose-gold",
    name: "Rose Gold",
    config: {
      type: "linear",
      angle: 45,
      colors: [
        { color: "#3D2314", offset: 0 },     // Deep bronze
        { color: "#B76E79", offset: 0.5 },   // Rose gold
        { color: "#E8C8B8", offset: 1 },     // Soft champagne
      ],
    },
    cssPreview: "linear-gradient(45deg, #3D2314 0%, #B76E79 50%, #E8C8B8 100%)",
  },
  {
    id: "aurora-mesh",
    name: "Aurora Mesh",
    config: {
      type: "linear",
      angle: 135,
      colors: [
        { color: "#4A1A6B", offset: 0 },     // Deep purple
        { color: "#0D7377", offset: 0.5 },   // Teal
        { color: "#E06B9E", offset: 1 },     // Pink
      ],
    },
    cssPreview: "linear-gradient(135deg, #4A1A6B 0%, #0D7377 50%, #E06B9E 100%)",
  },
  {
    id: "light-minimal",
    name: "Light Minimal",
    config: {
      type: "linear",
      angle: 180,
      colors: [
        { color: "#FFFFFF", offset: 0 },     // Pure white
        { color: "#F5F5F5", offset: 0.5 },   // Light gray
        { color: "#E8EDF2", offset: 1 },     // Soft blue-gray
      ],
    },
    cssPreview: "linear-gradient(180deg, #FFFFFF 0%, #F5F5F5 50%, #E8EDF2 100%)",
  },
  {
    id: "soft-pastel",
    name: "Soft Pastel",
    config: {
      type: "linear",
      angle: 90,
      colors: [
        { color: "#E6E6FA", offset: 0 },     // Soft lavender
        { color: "#B5EAD7", offset: 0.5 },   // Mint
        { color: "#FFE4C9", offset: 1 },     // Peach
      ],
    },
    cssPreview: "linear-gradient(90deg, #E6E6FA 0%, #B5EAD7 50%, #FFE4C9 100%)",
  },
  {
    id: "mountain-mist",
    name: "Mountain Mist",
    config: {
      type: "linear",
      angle: 180,
      colors: [
        { color: "#2C3E50", offset: 0 },     // Dark slate blue
        { color: "#6B8E9F", offset: 0.5 },   // Steel blue
        { color: "#BDC3C7", offset: 1 },     // Silver mist
      ],
    },
    cssPreview: "linear-gradient(180deg, #2C3E50 0%, #6B8E9F 50%, #BDC3C7 100%)",
  },
  {
    id: "midnight-wine",
    name: "Midnight Wine",
    config: {
      type: "linear",
      angle: 135,
      colors: [
        { color: "#1A0A0A", offset: 0 },     // Near black
        { color: "#4A1528", offset: 0.5 },   // Deep burgundy
        { color: "#722F37", offset: 1 },     // Wine red
      ],
    },
    cssPreview: "linear-gradient(135deg, #1A0A0A 0%, #4A1528 50%, #722F37 100%)",
  },
  {
    id: "obsidian-storm",
    name: "Obsidian Storm",
    config: {
      type: "linear",
      angle: 180,
      colors: [
        { color: "#0D0D0D", offset: 0 },     // Deep charcoal
        { color: "#2D2D3A", offset: 0.6 },   // Storm grey
        { color: "#4A4A6A", offset: 1 },     // Purple-grey
      ],
    },
    cssPreview: "linear-gradient(180deg, #0D0D0D 0%, #2D2D3A 60%, #4A4A6A 100%)",
  },
  {
    id: "cotton-candy",
    name: "Cotton Candy",
    config: {
      type: "linear",
      angle: 135,
      colors: [
        { color: "#FFDEE9", offset: 0 },     // Soft pink
        { color: "#B5FFFC", offset: 1 },     // Light cyan
      ],
    },
    cssPreview: "linear-gradient(135deg, #FFDEE9 0%, #B5FFFC 100%)",
  },

  // ============ RADIAL GRADIENTS (6) ============
  {
    id: "neon-pulse",
    name: "Neon Pulse",
    config: {
      type: "radial",
      center: "center",
      radius: 1.2,
      colors: [
        { color: "#FF006E", offset: 0 },
        { color: "#8338EC", offset: 1 },
      ],
    },
    cssPreview: "radial-gradient(circle at center, #FF006E 0%, #8338EC 100%)",
  },
  {
    id: "aurora-night",
    name: "Aurora Night",
    config: {
      type: "radial",
      center: "center",
      radius: 1.5,
      colors: [
        { color: "#2E1A47", offset: 0 },
        { color: "#0D7377", offset: 1 },
      ],
    },
    cssPreview: "radial-gradient(circle at center, #2E1A47 0%, #0D7377 100%)",
  },
  {
    id: "sunset-circle",
    name: "Sunset Circle",
    config: {
      type: "radial",
      center: "center",
      radius: 0.8,
      colors: [
        { color: "#FFC93C", offset: 0 },
        { color: "#FF5733", offset: 1 },
      ],
    },
    cssPreview: "radial-gradient(circle at center, #FFC93C 0%, #FF5733 100%)",
  },
  {
    id: "deep-ocean",
    name: "Deep Ocean",
    config: {
      type: "radial",
      center: "center",
      radius: 1.0,
      colors: [
        { color: "#0A1628", offset: 0 },    // Midnight blue
        { color: "#0D7377", offset: 0.6 },  // Teal
        { color: "#14FFEC", offset: 1 },    // Cyan glow
      ],
    },
    cssPreview: "radial-gradient(circle at center, #0A1628 0%, #0D7377 60%, #14FFEC 100%)",
  },
  {
    id: "plasma-burst",
    name: "Plasma Burst",
    config: {
      type: "radial",
      center: "center",
      radius: 1.3,
      colors: [
        { color: "#FF006E", offset: 0 },     // Hot magenta
        { color: "#8338EC", offset: 0.25 },  // Electric purple
        { color: "#00F5D4", offset: 0.5 },   // Neon cyan
        { color: "#B5FF00", offset: 0.75 },  // Lime green
        { color: "#FF006E", offset: 1 },     // Back to magenta
      ],
    },
    cssPreview: "radial-gradient(circle at center, #FF006E 0%, #8338EC 25%, #00F5D4 50%, #B5FF00 75%, #FF006E 100%)",
  },
  {
    id: "inferno",
    name: "Inferno",
    config: {
      type: "radial",
      center: "center",
      radius: 1.2,
      colors: [
        { color: "#FFDD00", offset: 0 },     // Bright yellow (center/hottest)
        { color: "#FF6600", offset: 0.4 },   // Orange
        { color: "#CC0000", offset: 0.8 },   // Deep red
        { color: "#660000", offset: 1 },     // Dark red (edges)
      ],
    },
    cssPreview: "radial-gradient(circle at center, #FFDD00 0%, #FF6600 40%, #CC0000 80%, #660000 100%)",
  },

  // ============ SWEEP GRADIENTS (3) ============
  {
    id: "cherry-blossom",
    name: "Cherry Blossom",
    config: {
      type: "sweep",
      center: "center",
      startAngle: 0,
      endAngle: 6.28, // Full circle (2π radians)
      colors: [
        { color: "#FFB3D9", offset: 0 },
        { color: "#E91E63", offset: 0.5 },
        { color: "#FFB3D9", offset: 1 },
      ],
    },
    cssPreview: "conic-gradient(from 0deg at center center, #FFB3D9 0deg, #E91E63 180deg, #FFB3D9 360deg)",
  },
  {
    id: "rainbow-wheel",
    name: "Rainbow Wheel",
    config: {
      type: "sweep",
      center: "center",
      startAngle: 0,
      endAngle: 6.28, // Full circle
      colors: [
        { color: "#FF0000", offset: 0 },     // Red
        { color: "#FF7F00", offset: 0.167 }, // Orange
        { color: "#FFFF00", offset: 0.333 }, // Yellow
        { color: "#00FF00", offset: 0.5 },   // Green
        { color: "#0000FF", offset: 0.667 }, // Blue
        { color: "#8B00FF", offset: 0.833 }, // Violet
        { color: "#FF0000", offset: 1 },     // Back to red
      ],
    },
    cssPreview: "conic-gradient(from 0deg at center center, #FF0000 0deg, #FF7F00 60deg, #FFFF00 120deg, #00FF00 180deg, #0000FF 240deg, #8B00FF 300deg, #FF0000 360deg)",
  },
  {
    id: "unicorn-dream",
    name: "Unicorn Dream",
    config: {
      type: "sweep",
      center: "center",
      startAngle: 0,
      endAngle: 6.28, // Full circle
      colors: [
        { color: "#FFB6C1", offset: 0 },     // Light pink
        { color: "#E6E6FA", offset: 0.2 },   // Lavender
        { color: "#98FF98", offset: 0.4 },   // Mint green
        { color: "#87CEEB", offset: 0.6 },   // Sky blue
        { color: "#FFDAB9", offset: 0.8 },   // Peach
        { color: "#FFB6C1", offset: 1 },     // Back to pink
      ],
    },
    cssPreview: "conic-gradient(from 0deg at center center, #FFB6C1 0deg, #E6E6FA 72deg, #98FF98 144deg, #87CEEB 216deg, #FFDAB9 288deg, #FFB6C1 360deg)",
  },
];

// Helper to get preset by ID
export function getPresetById(id: string): GradientPreset | undefined {
  return gradientPresets.find(preset => preset.id === id);
}
